<script lang="ts" setup>
import { Image, Popup } from "vant";
import { ref } from "vue";
import LightArrowIcon from "@/components/icon/LightArrowIcon.vue";
import { isImage, isMobile, isVideo } from "@/modules/common";
import i18n from "@/locales";
import { Media, Story } from "@/types/entity";

const props = defineProps<{
  onScreenshotGenerate(): Promise<Media>; // 图片生成方法
  story: Story;
  mediaId?: number;
}>();

const show = ref(false);
const loading = ref(false);
const url = ref("");
const videoRef = ref<HTMLVideoElement | null>(null);
let _media = ref<Media | undefined>(undefined);
const startGenerateScreenshot = async (
  media?: Media
): Promise<Media | void> => {
  show.value = true;

  if (media?.url) {
    _media.value = media;
    url.value = media?.url;
    return;
  }
  loading.value = true;
  try {
    loading.value = true;
    const media = await props.onScreenshotGenerate();
    url.value = media.url;
    return media;
  } finally {
    loading.value = false;
  }
};

const toggleShow = (val: boolean) => {
  show.value = val;
  if (!val) {
    videoRef.value?.pause();
  }
};

export interface ScreenshotSharePopupExpose {
  startGenerateScreenshot(media?: Media): Promise<Media | void>;
}

defineExpose<ScreenshotSharePopupExpose>({
  startGenerateScreenshot,
});

const showVideoPublish = ref(false);
</script>
<template>
  <popup
    :show="show"
    class="h-[90%] flex items-center overflow-hidden justify-center"
    position="bottom"
    teleport="body"
    @update:show="toggleShow"
  >
    <div
      v-if="loading"
      class="flex flex-col items-center justify-center gap-4 p-6 h-[200px] relative"
    >
      <span class="loading loading-infinity loading-lg text-primary"></span>
      <div class="text-base">{{ $t("Message.generatingLongImage") }}</div>
    </div>
    <div v-else class="w-full h-full grid grid-rows-[auto_1fr] overflow-hidden">
      <div class="navbar h-top-nav border-b">
        <!-- 左 -->
        <div class="navbar-start h-full">
          <button class="btn btn-ghost" @click="show = false">
            <light-arrow-icon />
          </button>
        </div>
        <div class="navbar-center">
          <div class="font-bold">
            {{
              isMobile()
                ? i18n.global.t("Message.longPressSaveImage")
                : i18n.global.t("Message.rightClickImage")
            }}
          </div>
        </div>
        <div class="navbar-end">
          <button
            v-if="isVideo(url)"
            class="btn btn-primary btn-sm"
            @click="showVideoPublish = true"
          >
            {{ $t(`Buttons.shareToMoments`) }}
          </button>
        </div>
      </div>
      <div class="overflow-y-auto bg-base-200">
        <div
          :class="{ 'border border-primary my-4 overflow-hidden': !isMobile() }"
          class="max-w-[512px] rounded-lg mx-auto h-fit flex justify-center"
        >
          <Image
            v-if="isImage(url)"
            :src="url"
            class="w-full"
            crossorigin="anonymous"
          />
          <video
            v-else
            ref="videoRef"
            :src="url"
            autoplay
            controls
            muted
            crossorigin="anonymous"
          ></video>
        </div>
      </div>
    </div>
  </popup>

  <VideoPublishModal
    v-if="_media?.id"
    v-model:show="showVideoPublish"
    :media-id="_media?.id!"
    :story="story"
  />
</template>
